<template>
  <div class="my-4 md:mx-8">
    <breadcrumbs />
    <div class="flex w-full items-center">
      <v-icon 
        color="primary" 
        class="text-6xl ic-management-mobile-phone" />
      <div class="uppercase font-bold text-grey text-base">
        {{ $t('messages.products.mobile_phone_management') }}
      </div>
    </div>
    <div class="">
      <div class="bg-white rounded-lg elevation-0 p-4">
        <!-- Debit Account -->
        <div>                    
          <v-subheader
            :class="`${colors.textPrimary}`"
            class="uppercase text-md font-bold p-0"
          >
            {{ $t('messages.accounts.account_detail') }}
          </v-subheader>

          <div class="flex flex-col w-full text-grey md:w-1/2">
            <label
              class="first-case text-grey text-md font-medium md:text-lg"
            >
              {{ $t('messages.accounts.account_number') }}
            </label>
            <div
              class="flex-1 mx-1 break-words">
              {{ account_number }}
            </div>
          </div>
          <div class="flex flex-col w-full text-grey md:w-1/2">
            <label
              class="first-case text-grey text-md font-medium md:text-lg"
            >
              {{ $t('messages.common.product') }}
            </label>
            <div
              class="flex-1 mx-1 break-words">
              {{ product }}
            </div>
          </div>
        </div>
        <!-- ./ Debit Account -->

        <!-- Mobile Phone To Link -->
        <div>
          <v-subheader 
            v-if="action === 'LINK'"
            :class="`${colors.textPrimary}`"
            class="uppercase text-md font-bold p-0"
          >
            {{ $t('messages.common.actions.mobile_phone_to_link') }}
          </v-subheader>
          <v-subheader 
            v-if="action === 'UNLINK'"
            :class="`${colors.textPrimary}`"
            class="uppercase text-md font-bold p-0"
          >
            {{ $t('messages.common.actions.mobile_phone_to_unlink') }}
          </v-subheader>
          <div class="flex flex-wrap">                      
            <div 
              v-if="action === 'LINK'" 
              class="w-full md:pr-3 md:w-1/2">
              <phone-number-input
                :default-country-code="form.beneficiaryPhoneCountryCode"
                :national-mode-operation="true"
                v-model="form.mobilePhone"
                @formatted="setPhoneNumberToSend"
              >
                <template slot="label">
                  {{ $t('messages.common.phone_number') }}
                  {{ $t('messages.info_msgs.optional') }}
                </template>
              </phone-number-input>
            </div>
            <div 
              v-if="action === 'UNLINK'" 
              class="w-full md:pr-3 md:w-1/2">
              <div
                class="flex-1 mx-1 break-words">
                {{ mobile_phone }}
              </div>
            </div>
          </div>
        </div>
        <!-- ./ Mobile Phone To Link -->

      </div>
      <div
        class="flex flex-col-reverse justify-end mt-3 -mr-1 sm:flex-row"
      >
        <v-flex class="flex-col flex-col-reverse sm:flex-row justify-end">
          <v-btn
            :disabled="processing"
            :to="{ name: 'management-mobile-phone' }"
            class="elevation-0 bg-grey text-white rounded-xl font-bold">{{ $t("messages.common.actions.back") }}</v-btn>
          <v-btn
            :disabled="disabledVerificationButton"
            :class="colors.primaryBackground"
            class="elevation-0 text-white rounded-xl font-bold"            
            @click="warnLink">
            {{ (action === "LINK") ? this.$i18n.t("messages.common.actions.link") : this.$i18n.t("messages.common.actions.unlink") }}
          </v-btn>
        </v-flex>        
      </div>
      <LinkPhoneNumberDialog
        :phone-number="phoneNumberToSend"
        :show="linkPhoneNumberDialog"
        @onClose="() => { linkPhoneNumberDialog = false; }"
        @onSuccess="registerMobilePhoneOnAccount"
      />
      <YesNoDialog
        :show="yesNoDialog"
        @onYes="link"
        @onNo="yesNoDialog = !yesNoDialog">
        <template slot="title">
          <v-icon
            :class="colors.textPrimary"
            class="text-7xl">ic-warning</v-icon>
        </template>
        <template slot="content">
          <div class="break-words text-center px-6 first-case">
            <template>
              <p class="text-lg font-medium mt-2">
                {{ action === "LINK" ? $t("messages.info_msgs.confirmation_required_msg_link", {
                  mobile_phone: form.mobilePhone,
                  product: product,
                  account_number: account_number
                }) :
                $t("messages.info_msgs.confirmation_required_msg_unlink", {
                  mobile_phone: form.mobilePhone,
                  product: product,
                  account_number: account_number
                })
                }}
              </p>
            </template>
          </div>
        </template>
        <template slot="yes-text">{{ $t("messages.common.actions.continue") }}</template>
        <template slot="no-text">{{ $t("messages.common.actions.close") }}</template>
      </YesNoDialog>   
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions, mapState } from 'vuex';
import { isEmpty } from 'lodash';
const PhoneNumberInput = () => import('@/components/PhoneNumberInput');

export default {
  name: 'MobilePhoneManagementForm',
   components: {
    PhoneNumberInput
  },
  props: {
    account_number: {
      type: String,
      default: '',
      required: true
    },
    // reference_id
    product: {
      type: String,
      default: '',
      required: true
    },
    action: {
      type: String,
      default: 'LINK',
      required: true
    },
    reference_id: {
      type: Number,      
      required: true
    },
    mobile_phone: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      form: {
        beneficiaryPhoneCountryCode: "sv",
        mobilePhone: ""
      },
      yesNoDialog: false,
      processing: false,
      linkPhoneNumberDialog: false,
      phoneNumberToSend: "",
      phoneNumberLength: 8,
      sharedKey: null,
      disabledVerificationButton: false
    };
  },
  computed: {
    ...mapGetters({
      colors: 'currentColors',
      menuChilds: "general/menuChilds"
    }),
    ...mapState({
      dialog: (state) => state.dialog,
      countries: (state) => state.general.countries
    }),    
    currentMenu() {
      return this.menuChilds.find(m => m.name === this.$route.name);
    }
  },
  methods: {
    ...mapActions({
      sendOTPCode: 'auth/sendOTPCode',
      linkOTPPhone: 'auth/linkOTPPhone'
    }),
    warnLink() {
      if (this.action === "UNLINK") {
        this.setPhoneNumberToSendUnlink(this.mobile_phone);
      }
      if (isEmpty(this.phoneNumberToSend)) {
        this.$store.dispatch(
            'dialog',
            {
              title: 'Advertencia',
              message: 'Número de teléfono es requerido',
              type: 'error'
            },
            { root: true }
          );   
        return;
      }

      if (this.phoneNumberToSend.length < this.phoneNumberLength) {
        this.$store.dispatch(
            'dialog',
            {
              title: 'Advertencia',
              message: 'Número de teléfono inválido',
              type: 'error'
            },
            { root: true }
          );   
        return;
      }

       var strongRegex = new RegExp(
      //eslint-disable-next-line no-useless-escape
        "^[6-7][0-9]{7}$"
        );
      
      var validateFormat = this.action === "LINK" ? strongRegex.test(this.form.mobilePhone) : strongRegex.test(this.form.mobilePhone.slice(-this.phoneNumberLength));
      if (!validateFormat) {
        this.$store.dispatch(
            'dialog',
            {
              title: 'Advertencia',
              message: 'No se pueden vincular números de teléfono fijo.',
              type: 'error'
            },
            { root: true }
          );   
        return;
      }

      this.yesNoDialog = true;
    },
    async link() {
      this.yesNoDialog = false;
      this.linkPhoneNumberDialog = this.action === "LINK" ? true : false;
      if (this.action === "LINK")
        this.sharedKey = await this.sendOTPCode({ mobile_phone: this.form.mobilePhone } );
      else
        this.registerMobilePhoneOnAccount("","");
    },
    async registerMobilePhoneOnAccount({ otp_code, shared_key }) {
      try {        
        await this.linkOTPPhone({
          reference_id: this.reference_id,
          otp_code,
          data_shared_key: this.sharedKey !== null ? this.sharedKey : shared_key,
          action: this.action,
          mobile_phone: this.form.mobilePhone
          
        });
        this.$store.dispatch(
              'dialog',
              {
                title: this.$i18n.t('messages.info_msgs.successful_operation_code'),
                message: `El número ${this.form.mobilePhone} ha sido ${this.action ==="LINK" ? "vinculado a tu": "desvinculado de tu"} ${this.product} número ${this.account_number}`,
                type: 'success'
              },
              { root: true }
        );
        this.disabledVerificationButton = true;
      } catch (error) {
        this.$store.dispatch(
          'dialog',
          {
            title: 'Operación fallida',
            message: error,
            type: 'error'
          },
          { root: true }
        );
      }
    },
    setPhoneNumberToSend(formatted) {
      this.phoneNumberLength = formatted.mask.length;
      this.phoneNumberToSend = formatted.formattedNumber;
    },
    setPhoneNumberToSendUnlink(formatted) {
      this.phoneNumberLength=8;     
      this.phoneNumberToSend = formatted;
      this.form.mobilePhone = this.phoneNumberToSend;
    }
  }
};
</script>

<style></style>
